
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOMetadata from '@/components/SEOMetadata';
import { Calendar, User, ArrowLeft, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getArticleBySlug, getRelatedArticles } from '@/data/proxyArticles';

const BlogPost = () => {
  const { slug } = useParams();
  const post = slug ? getArticleBySlug(slug) : undefined;

  // Legacy hardcoded posts fallback
  const legacyPosts: Record<string, any> = {
    'residential-proxies-travel-industry-ethics': {
      title: 'The Quiet Power of Residential Proxies',
      author: 'NEXX Industry Insights',
      date: '2024-05-20',
      readTime: '7 min read',
      category: 'Industry',
      image: '/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png',
      content: `<p class="text-lg leading-relaxed mb-6">I've worked with proxies in a few different capacities over the years. Everything from scraping market data for competitive insights to automating tasks that would make most people want to pull their hair out. But it wasn't until I started looking deeper into the travel sector that I realized just how powerful <a href="https://nexx.net/residential-proxies" class="text-nexx-green hover:text-green-700 underline">residential proxies</a> can be.</p><p class="mb-6">Used ethically, they can improve access, create fairness, and support smart decision making.</p>`
    },
    'data-center-security-best-practices': {
      title: 'Data Center Security Best Practices for 2024',
      author: 'NEXX Technical Team',
      date: '2024-05-15',
      readTime: '8 min read',
      category: 'Security',
      image: '/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png',
      content: `<p class="text-lg leading-relaxed mb-6">In today's digital landscape, data center security has become more critical than ever.</p>`
    },
    'choosing-right-proxy-solution': {
      title: 'Choosing the Right Proxy Solution for Your Business',
      author: 'NEXX Network Team',
      date: '2024-05-10',
      readTime: '6 min read',
      category: 'Networking',
      image: '/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png',
      content: `<p class="text-lg leading-relaxed mb-6">With the increasing importance of data privacy, web scraping, and global content access, choosing the right proxy solution has become crucial.</p>`
    }
  };

  const legacyPost = slug ? legacyPosts[slug] : undefined;

  if (!post && !legacyPost) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex-1 pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
            <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
            <Link to="/blog">
              <Button className="bg-nexx-green hover:bg-green-700 text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Use new article data if available, otherwise legacy
  const title = post?.title ?? legacyPost.title;
  const author = post?.author ?? legacyPost.author;
  const date = post?.date ?? legacyPost.date;
  const readTime = post?.readTime ?? legacyPost.readTime;
  const category = post?.category ?? legacyPost.category;
  const content = post?.content ?? legacyPost.content;
  const metaDescription = post?.metaDescription ?? `${title.substring(0, 150)}...`;
  const metaTitle = post?.metaTitle ?? `${title} | NEXX Blog`;
  const keywords = post?.keywords?.join(', ') ?? `${category.toLowerCase()}, NEXX`;

  const relatedArticles = post ? getRelatedArticles(slug!, 3) : [];

  const getServiceLink = (service: string): string => {
    const serviceMap: Record<string, string> = {
      'Residential Proxies': '/residential-proxies',
      'VPS Servers': '/vps-servers',
      'Baremetal Servers': '/baremetal-servers',
      'Mobile Solutions': '/mobile-solutions',
      'Consultation': '/consultation',
      'Data Center': '/ashburn-interconnect',
    };
    return serviceMap[service] || '/consultation';
  };

  return (
    <>
      <SEOMetadata
        title={metaTitle}
        description={metaDescription}
        keywords={keywords}
        ogTitle={title}
        ogDescription={metaDescription}
        canonical={`https://nexx.net/blog/${slug}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": title,
          "description": metaDescription,
          "author": { "@type": "Organization", "name": author },
          "publisher": { "@type": "Organization", "name": "NEXX", "url": "https://nexx.net" },
          "datePublished": date,
          "dateModified": date,
          "url": `https://nexx.net/blog/${slug}`,
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://nexx.net/blog/${slug}` }
        }}
      />
      <div className="min-h-screen bg-gray-50">
        <Header />
      
        {/* Hero header */}
        <section className="pt-24 pb-12 bg-gradient-to-br from-nexx-green to-green-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="inline-flex items-center text-green-100 hover:text-white mb-6 text-sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
            <div className="flex flex-wrap items-center gap-3 text-sm text-green-100 mb-4">
              <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                {category}
              </span>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{new Date(date).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{readTime}</span>
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {title}
            </h1>
            <div className="flex items-center gap-2 text-green-100">
              <User className="h-4 w-4" />
              <span>By {author}</span>
            </div>
          </div>
        </section>

        {/* Article body */}
        <div className="pb-16">
          <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-10 md:p-12">
              <div 
                className="prose prose-lg max-w-none
                  prose-headings:text-gray-900 prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-5
                  prose-a:text-nexx-green prose-a:underline hover:prose-a:text-green-700
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-5 prose-ul:space-y-2
                  prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-5 prose-ol:space-y-2
                  prose-li:text-gray-700
                  prose-strong:text-gray-900
                  prose-em:text-gray-600
                  prose-blockquote:border-l-4 prose-blockquote:border-nexx-green prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600
                  [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-gray-900 [&>h2]:mb-4 [&>h2]:mt-10
                  [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-gray-900 [&>h3]:mb-3 [&>h3]:mt-8
                  [&>p]:mb-5 [&>p]:text-gray-700 [&>p]:leading-relaxed [&>p]:text-base
                  [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-5 [&>ul]:space-y-2
                  [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-5 [&>ol]:space-y-2
                  [&_li]:text-gray-700 [&_li]:text-base
                  [&_a]:text-nexx-green [&_a]:underline [&_a:hover]:text-green-700
                  [&_strong]:text-gray-900 [&_strong]:font-semibold
                  [&_em]:text-gray-600
                  [&_blockquote]:border-l-4 [&_blockquote]:border-nexx-green [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-6"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>

            {/* Related Services */}
            {post?.relatedServices && post.relatedServices.length > 0 && (
              <div className="mt-8 p-8 bg-white rounded-2xl shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Explore Related Services</h3>
                <div className="flex flex-wrap gap-3">
                  {post.relatedServices.map((service, index) => (
                    <Link
                      key={index}
                      to={getServiceLink(service)}
                      className="inline-block px-4 py-2 bg-green-50 hover:bg-green-100 text-nexx-green rounded-lg transition-colors text-sm font-medium"
                    >
                      {service}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/blog/${ra.slug}`}
                      className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-shadow"
                    >
                      <span className="text-xs font-medium text-nexx-green">{ra.category}</span>
                      <h4 className="text-sm font-bold text-gray-900 mt-1 mb-2 line-clamp-2">{ra.title}</h4>
                      <p className="text-xs text-gray-500">{ra.readTime}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8 p-8 bg-white rounded-2xl shadow-md text-center">
              <p className="text-gray-600 mb-4">
                Want to learn more about our proxy solutions?
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/residential-proxies">
                  <Button className="bg-nexx-green hover:bg-green-700 text-white w-full sm:w-auto">
                    Explore Residential Proxies
                  </Button>
                </Link>
                <Link to="/consultation">
                  <Button variant="outline" className="w-full sm:w-auto">
                    Schedule a Consultation
                  </Button>
                </Link>
              </div>
            </div>
          </article>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default BlogPost;
